import * as React from "react";

const isBrowser = typeof window !== "undefined";
const runTransition =
    typeof React.startTransition === "function"
        ? React.startTransition
        : (callback) => callback();

const sanitizeSizing = (value) => {
    if (!value || typeof value !== "object") {
        return {};
    }
    return {...value};
};

const shallowEqual = (a, b) => {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
        return false;
    }
    for (let index = 0; index < aKeys.length; index += 1) {
        const key = aKeys[index];
        if (a[key] !== b[key]) {
            return false;
        }
    }
    return true;
};

export const useColumnSizingManager = (initialSizing = {}) => {
    const [columnSizing, setColumnSizingState] = React.useState(() => sanitizeSizing(initialSizing));
    const columnSizingRef = React.useRef(columnSizing);
    const pendingSizingRef = React.useRef(null);
    const rafRef = React.useRef(null);

    const applySizing = React.useCallback((nextSizing) => {
        const sanitized = sanitizeSizing(nextSizing);
        columnSizingRef.current = sanitized;
        runTransition(() => {
            setColumnSizingState(sanitized);
        });
        return sanitized;
    }, []);

    const cancelScheduledFrame = React.useCallback(() => {
        if (rafRef.current !== null && isBrowser) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    const flushPendingSizing = React.useCallback(() => {
        if (!pendingSizingRef.current) {
            return columnSizingRef.current;
        }
        const nextSizing = pendingSizingRef.current;
        pendingSizingRef.current = null;
        cancelScheduledFrame();
        return applySizing(nextSizing);
    }, [applySizing, cancelScheduledFrame]);

    React.useEffect(() => () => {
        flushPendingSizing();
        cancelScheduledFrame();
    }, [flushPendingSizing, cancelScheduledFrame]);

    const scheduleFrame = React.useCallback(() => {
        if (!isBrowser) {
            flushPendingSizing();
            return;
        }
        if (rafRef.current !== null) {
            return;
        }
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            flushPendingSizing();
        });
    }, [flushPendingSizing]);

    const setColumnSizing = React.useCallback((updater) => {
        const base = pendingSizingRef.current ?? columnSizingRef.current;
        const nextValue = typeof updater === "function" ? updater(base) : updater;
        const sanitized = sanitizeSizing(nextValue);

        if (shallowEqual(base, sanitized)) {
            return base;
        }

        pendingSizingRef.current = sanitized;
        scheduleFrame();
        return sanitized;
    }, [scheduleFrame]);

    const replaceColumnSizing = React.useCallback((nextSizing = {}) => {
        const sanitized = sanitizeSizing(nextSizing);
        if (shallowEqual(columnSizingRef.current, sanitized)) {
            return columnSizingRef.current;
        }
        pendingSizingRef.current = null;
        cancelScheduledFrame();
        return applySizing(sanitized);
    }, [applySizing, cancelScheduledFrame]);

    return {
        columnSizing,
        setColumnSizing,
        replaceColumnSizing,
        flushPendingSizing,
    };
};

